import React from 'react';
import {Icon, Text, Toggle, WithTooltip, Skeleton} from '@albert-io/atomic';

import type {AdminSchool} from './useAdminSchools';
import {usePreviewLockImpactQuery} from './previewLockImpact.queries.generated';
import {
  GUIDE_LEVEL_LOCK_TYPE,
  NON_PRO_TOGGLE_TOOLTIP,
  PENDING_REMOVAL_TOGGLE_TOOLTIP
} from './manageRestrictionsModal.constants';
import {
  getRestrictionRemovalDate,
  hasScheduledRestrictionRemoval,
  removalWarningMessage
} from './proExpiry';

interface SchoolRestrictionRowProps {
  school: AdminSchool;
  guideLevelId?: string;
  isRestricted: boolean;
  /**
   * Whether the toggle is operable — requires an active pro license and no running grace window:
   * the backend's ContentLock policies gate both `lock` and `destroy` on `SchoolHasProLicense`,
   * and a school with a running grace row is lapsing regardless of what the stored license says,
   * so its restrictions can only be observed until the removal date, not adjusted.
   */
  canAdjust: boolean;
  onToggle: () => void;
}

// Per-school impact copy, per the Figma (1519-20440): "Questions will be blocked in x closed
// assignments and y active assignment." The zero-impact sentence isn't in the design; kept from
// the earlier iteration so an impactless school still reads deliberately.
const getImpactMessage = (closedAssignments: number, openAssignments: number): string => {
  if (closedAssignments === 0 && openAssignments === 0) {
    return 'Restricting will not impact any active or closed assignments.';
  }
  const closedClause = `${closedAssignments} closed ${
    closedAssignments === 1 ? 'assignment' : 'assignments'
  }`;
  const activeClause = `${openAssignments} active ${
    openAssignments === 1 ? 'assignment' : 'assignments'
  }`;
  if (closedAssignments > 0 && openAssignments > 0) {
    return `Questions will be blocked in ${closedClause} and ${activeClause}.`;
  }
  return `Questions will be blocked in ${closedAssignments > 0 ? closedClause : activeClause}.`;
};

export const SchoolRestrictionRow = ({
  school,
  guideLevelId,
  isRestricted,
  canAdjust,
  onToggle
}: SchoolRestrictionRowProps) => {
  // A lapsed school whose restrictions are still enforced (Part 9's grace window): warn on the
  // row with the schedule copy. Both react to the pending toggle — switching the restriction off
  // drops this row's warning immediately.
  const removalScheduled = hasScheduledRestrictionRemoval(school, isRestricted);
  const removalDate = removalScheduled ? getRestrictionRemovalDate(school) : null;

  const {data, loading} = usePreviewLockImpactQuery({
    variables: {lockType: GUIDE_LEVEL_LOCK_TYPE, sourceId: guideLevelId ?? '', schoolId: school.id},
    // No impact preview for non-Pro schools (restriction is disabled for them) — except with a
    // removal scheduled, where the still-enforced restriction's impact is exactly what's at stake.
    skip: !guideLevelId || (!school.hasPro && !removalScheduled)
  });
  const impact = data?.previewLockImpact;
  // Shown regardless of restrict state (per the Figma annotation); getImpactMessage owns the
  // no-impact copy too, so this is null only until the preview has loaded.
  const displayMessage = impact
    ? getImpactMessage(impact.closedAssignmentCount, impact.openAssignmentCount)
    : null;

  return (
    <tr className='border-border-subtle border-b last:border-b-0'>
      <td className='px-x2 py-x1-half w-full'>
        {/* The warning icon sits in its own gutter so the name and the sub-lines share one left
            edge (the icon must not indent the name alone). */}
        <div className='gap-x1 flex items-start'>
          {removalScheduled && (
            <Icon
              icon='exclamation-triangle'
              iconStyle='regular'
              color='warning'
              aria-hidden
              className='mt-x0-quarter'
              data-testid={`school-${school.id}-restriction-row__grace-warning`}
            />
          )}
          <div className='min-w-0 flex-1'>
            <Text as='div'>{school.name}</Text>
            {/* Reserve the impact line with a skeleton while in flight so the row height doesn't jump. */}
            <div className='mt-x0-quarter' aria-busy={loading}>
              {loading ? (
                <>
                  <Skeleton className='h-x1 rounded-x1' />
                  <span className='sr-only'>Loading assignment impact</span>
                </>
              ) : (
                displayMessage && (
                  <Text as='div' size='s' color='tertiary'>
                    {displayMessage}
                  </Text>
                )
              )}
            </div>
            {removalDate && (
              <Text as='div' size='s' color='warning'>
                {removalWarningMessage(removalDate)}
              </Text>
            )}
          </div>
        </div>
      </td>
      <td className='border-border-primary px-x2 py-x1-half border-l text-left align-middle whitespace-nowrap'>
        {canAdjust ? (
          <Toggle
            checked={isRestricted}
            onChange={onToggle}
            aria-label={`Restrict ${school.name}`}
          />
        ) : (
          <WithTooltip
            focusable
            content={
              school.restrictionRemovalDate
                ? PENDING_REMOVAL_TOGGLE_TOOLTIP
                : NON_PRO_TOGGLE_TOOLTIP
            }
          >
            <span className='cursor-not-allowed'>
              <Toggle checked={isRestricted} disabled aria-label={`Restrict ${school.name}`} />
            </span>
          </WithTooltip>
        )}
      </td>
    </tr>
  );
};

export default SchoolRestrictionRow;
